import { Injectable } from '@angular/core';
import { Mote, MoteService, RestService, ToastService, ToastType } from '@motes-sdk';
import { SupabaseService } from '@motes-sdk/providers/supabase.service';
import confetti from 'canvas-confetti';

@Injectable({
    providedIn: 'root',
})
export class PublishService {

    private readonly confettiParticleCount = 200;
    private readonly confettiDefaultOpts = {
        origin: { y: 0.7 }
    };

    constructor(
        private restService: RestService,
        private toastService: ToastService,
        private supabaseService: SupabaseService,
        private moteService: MoteService,
    ) {

    }

    private async setWorldCityId(tour: Mote) {
        // Set world city id if it is not already set
        if(!tour.worldCityId) {
            const db = await this.supabaseService.getSupabaseClient();
            // Lookup the closest world city in comparison to the tour lat and long
            const { data: findWorldCity, error } = await db.rpc('find_closest_world_city', { latitude: tour.position.lat, longitude: tour.position.long }).maybeSingle();
            if(error) console.warn("Could not find an appropriate world city id for the tour", error);

            // Save the world city id
            if(findWorldCity?.world_city_id) {
                tour.worldCityId = findWorldCity.world_city_id;
                await this.moteService.saveMoteWorldCityId(tour.key, tour.worldCityId);
            }
        }
    }

    /**
     * Publish a StoryHunt tour (and refresh tour page cache)
     */
    public async publishTour(tour: Mote, stories: Mote[]): Promise<boolean> {
        await this.setWorldCityId(tour);
        // Publish the tour
        const ok = await this.restService.publishTour(tour.key, true).catch(() => null);
        // Handle error
        if(!ok) {
            this.toastService.addToast($localize`Tour could not be published, please try again or contact customer service`, ToastType.ERR);
            return false;
        }

        const updatedTour = await this.moteService.getMote(tour.key);
        await this.syncTour(updatedTour, stories, { published: true });

        // Clear tour page cache
        this.toastService.addToast($localize`Please wait, refreshing tour page...`, ToastType.OK);
        const cacheResponse = await this.restService.clearTourCdnCache(tour.key, true).catch(err => []);
        // Re populate tour page cache
        await this.restService.populateTourCdnCache(cacheResponse.map(rsp => rsp.url));

        this.toastService.addToast($localize`Congratulations, tour published.`, ToastType.OK);
        this.realisticConfetti();
        return true;
    }

    /**
     * Publish changes to the Supabase prod server and sync to PTG.
     * @param options.forcePtgPreview When true (Preview / "Update link"), PTG gets preview only even if the tour is published.
     */
    public async publishChanges(tour: Mote, stories: Mote[], toastLabel: string = "", options?: { forcePtgPreview?: boolean }) {
        // Set world city id if it is not already set
        await this.setWorldCityId(tour);
        const ptg = options?.forcePtgPreview
            ? { preview: true as const }
            : tour.publishedOn
                ? { published: true as const }
                : { preview: true as const };
        await this.syncTour(tour, stories, ptg);

        if(!toastLabel) return; 
        this.toastService.addToast(toastLabel || $localize`Changes successfully published`, ToastType.OK);
    }

    /**
     * Push preview slice to PTG only (no Supabase/Firebase write). Used when copying the go.storyhunt.io preview link.
     */
    public async pushPreviewToPtgOnly(tourKey: string): Promise<boolean> {
        try {
            await this.restService.syncTourNewDb(tourKey, { preview: true });
            return true;
        } catch (error) {
            const errorMessage = this.getErrorMessage(error);
            const suffix = errorMessage ? ` (${errorMessage})` : "";
            console.warn("pushPreviewToPtgOnly failed", { tourKey, error });
            this.toastService.addToast($localize`Sorry, could not refresh preview on the new app` + suffix, ToastType.ERR);
            return false;
        }
    }

    /**
     * Unpublish a StoryHunt tour (and refresh tour page cache)
     */
    public async unpublishTour(tour: Mote, stories: Mote[]): Promise<boolean> {
        // Unpublish tour
        const ok = await this.restService.publishTour(tour.key, false).catch(() => null);
        if(!ok) {
            this.toastService.addToast($localize`Could not unpublish tour, please try again`, ToastType.ERR);
            return false;
        }

        const updatedTour = await this.moteService.getMote(tour.key);
        // Unpublish tour in supabase
        await this.syncTour(updatedTour, stories, { published: false });

        // Clear tour page cache
        this.toastService.addToast($localize`Please wait, refreshing tour page...`, ToastType.OK);
        const cacheResponse = await this.restService.clearTourCdnCache(tour.key, true).catch(err => []);
        // Re populate tour page cache
        await this.restService.populateTourCdnCache(cacheResponse.map(rsp => rsp.url));

        this.toastService.addToast($localize`Tour unpublished`, ToastType.OK);

        return Boolean(ok);
    }

    private realisticConfetti() {
        this.fireConfetti(0.25, {
            spread: 26,
            startVelocity: 55,
        });
        this.fireConfetti(0.2, {
            spread: 60,
        });
        this.fireConfetti(0.35, {
            spread: 100,
            decay: 0.91,
            scalar: 0.8
        });
        this.fireConfetti(0.1, {
            spread: 120,
            startVelocity: 25,
            decay: 0.92,
            scalar: 1.2
        });
        this.fireConfetti(0.1, {
            spread: 120,
            startVelocity: 45,
        });
    }

    private fireConfetti(particleRatio, opts) {
        confetti(Object.assign({}, this.confettiDefaultOpts, opts, {
            particleCount: Math.floor(this.confettiParticleCount * particleRatio)
        }));
    }

    private getErrorMessage(error: unknown): string {
        console.error(error);
        if (!error) return "";
        const anyError = error as { message?: string; error?: { err?: string; message?: string } };
        return anyError?.error?.err || anyError?.error?.message || anyError?.message || "";
    }

    private async syncTour(tour: Mote, stories: Mote[], ptg?: { published?: boolean; preview?: boolean }) {
        // Set a new updated on timestamp (+1 millisecond to promote sync detection)
        tour.updatedOn = new Date(tour.updatedOn.getTime() + 1);

        // Update / create tour in supabase db
        const supabase = await this.supabaseService.getSupabaseClient();
        const supabaseTour = this.supabaseService.convertTourToSupabase(tour);
        const { error } = await supabase.from('tours').upsert(supabaseTour, {
            onConflict: 'key'
        });

        // Create a copy of the tour snapshot in the migrations table
        const { error: migrationError } = await supabase.from('tours_migration').upsert(supabaseTour, {
            onConflict: 'key'
        });

        if(error || migrationError) {
            this.toastService.addToast($localize`Sorry, could not update tour on the public server (please contact customer service)`, ToastType.ERR);
            console.warn(error, migrationError);
            return;
        }

        // Sync stories to supabase
        await this.syncStories(stories);

        try {
            const syncNewDbOk = await this.restService.syncTourNewDb(tour.key, ptg);
            if(!syncNewDbOk) {
                this.toastService.addToast($localize`Sorry, could not update tour on the new database (please contact customer service)`, ToastType.ERR);
                return;
            }
        } catch (error) {
            const errorMessage = this.getErrorMessage(error);
            const suffix = errorMessage ? ` (${errorMessage})` : "";
            console.warn("syncTourNewDb failed", { tourKey: tour.key, ptg, error });
            this.toastService.addToast($localize`Sorry, could not update tour on the new database (please contact customer service)` + suffix, ToastType.ERR);
            return;
        }

        // Sync Webflow only when publishing live content (not preview-only updates).
        if(ptg?.published === true) {
            try {
                await this.restService.syncWebflowAfterPublish(tour.key, {});
            } catch (error) {
                const errorMessage = this.getErrorMessage(error);
                const suffix = errorMessage ? ` (${errorMessage})` : "";
                console.warn("syncWebflowAfterPublish failed", { tourKey: tour.key, error });
                this.toastService.addToast($localize`Sorry, could not sync tour to website` + suffix, ToastType.ERR);
            }
        }

        // Invoke a change in the firebase tour for listeners (header) to detect sync
        await this.moteService.setUpdatedOn(tour.key, tour.updatedOn.getTime());
    }

    private async syncStories(stories: Mote[]) {
        // Convert stories to supabase
        const supabaseStories = stories.map(story => this.supabaseService.convertStoryToSupabase(story));

        // Update / create stories in supabase db
        const supabase = await this.supabaseService.getSupabaseClient();
        const { error } = await supabase.from('stories').upsert(supabaseStories, {
            onConflict: 'key'
        });

        // Create a copy of the stories snapshot in the migrations table
        const { error: migrationError } = await supabase.from('stories_migration').upsert(supabaseStories, {
            onConflict: 'key'
        });

        if(error || migrationError) {
            this.toastService.addToast($localize`Sorry, could not publish stories on the public server (please contact customer service)`, ToastType.ERR);
            console.warn(error);
            return;
        }
    }
}
